import React, { useEffect } from 'react'

//#region Components
import styled from '@emotion/styled'
import { css, ClassNames } from '@emotion/react'

import { UserChip } from 'components/shared/UserChip'
import { ManWithTimeIcon } from 'assets/icons/ManWithTimeIcon'
import { LocationIcon } from 'assets/icons/LocationIcon'
import i18n from 'i18next'
import {
    LoaderOverlay,
    Spinner
} from 'components/shared/ThreeSectionAttendeeManager/attendeeStyledComponents'

import { ColorBaseGreyLight, ColorBaseDarkGray } from 'assets/styles/variables'

//#endregion

//#region Props

type Props = {
    section: any
    presentAttendees?: Attendee[]
    invitedAttendees?: Attendee[]
    absentAttendees?: Attendee[]
    attendeesInfo: string
    isAllExpanded: boolean
    loading?: boolean
}

//#endregion

//#region Implementation
export const MinutesAttendeesReadOnly = (props: Props) => {
    const {
        presentAttendees = [],
        invitedAttendees = [],
        absentAttendees = [],
        attendeesInfo,
        loading = false
    } = props
    // If expand all is toggled in the toolbar, set current isExpanded to that value
    useEffect(() => {}, [props.isAllExpanded])

    const renderAttendees = (attendees: Attendee[], hideIcons = false) => {
        return attendees.map((attendee) => {
            const attendeeAny = attendee as any
            return (
                <UserChip
                    key={attendee.id}
                    name={attendee.display_name}
                    email={attendee.email || i18n.t('NO_EMAIL_PERSON_LABEL')}
                    readonly
                    hideIcons={hideIcons}
                    {...(attendeeAny.isTimeLate && {
                        interactiveIcon: <ManWithTimeIcon />
                    })}
                    {...(attendeeAny.isLocationRemote && {
                        secondaryInteractiveIcon: <LocationIcon />
                    })}
                    isTimeLate={attendeeAny.isTimeLate}
                    isLocationRemote={attendeeAny.isLocationRemote}
                />
            )
        })
    }

    return (
        <ClassNames>
            {({ css }) => (
                <AttendeesSection>
                    <SectionContent>
                        {/* Present Section */}
                        <label
                            aria-label={i18n.t('ATTENDANCE_PRESENT')}
                            className={css`
                                ${labelStyle}
                            `}>
                            {i18n.t('ATTENDANCE_PRESENT')}
                        </label>
                        <ChipArea>
                            {renderAttendees(presentAttendees)}
                            {loading && (
                                <LoaderOverlay>
                                    <Spinner />
                                </LoaderOverlay>
                            )}
                        </ChipArea>

                        {/* Attending/Invited Section */}
                        <div style={{ marginTop: '24px' }}>
                            <label
                                aria-label={i18n.t('INVITED_ATTENDEES')}
                                className={css`
                                    ${labelStyle}
                                `}
                                style={{ marginTop: '24px' }}>
                                {i18n.t('INVITED_ATTENDEES')}
                            </label>
                            <ChipArea>
                                {renderAttendees(invitedAttendees)}
                                {loading && (
                                    <LoaderOverlay>
                                        <Spinner />
                                    </LoaderOverlay>
                                )}
                            </ChipArea>
                        </div>

                        {/* Apologies/Absent Section */}
                        <div style={{ marginTop: '24px' }}>
                            <label
                                aria-label={i18n.t('ATTENDANCE_ABSENT')}
                                className={css`
                                    ${labelStyle}
                                `}
                                style={{ marginTop: '24px' }}>
                                {i18n.t('ATTENDANCE_ABSENT')}
                            </label>
                            <ChipArea>
                                {renderAttendees(absentAttendees, true)}
                                {loading && (
                                    <LoaderOverlay>
                                        <Spinner />
                                    </LoaderOverlay>
                                )}
                            </ChipArea>
                        </div>

                        <div style={{ marginTop: '20px' }}>
                            <label
                                aria-label={i18n.t('ATTENDANCE_NOTES')}
                                className={css`
                                    ${labelStyle}
                                `}>
                                {i18n.t('ATTENDANCE_NOTES')}
                            </label>
                            <AdditionalInfo>{attendeesInfo}</AdditionalInfo>
                        </div>
                    </SectionContent>
                </AttendeesSection>
            )}
        </ClassNames>
    )
}

const AttendeesSection = styled('div')`
    display: flex;
    flex-flow: column;
    margin-top: 2px;
    width: 90vw;
    margin: 20px;
`
//    height: 45px;
const SectionContent = styled('div')`
    margin: 20px 24px 0px;
`
const ChipArea = styled('div')`
    border: 1px solid #949494;
    border-radius: 4px;
    padding: 0px 20px 0px 16px;
    margin-top: 8px;
    min-height: 90px;
    position: relative;
`
const AdditionalInfo = styled('div')`
    border: 1px solid #949494;
    border-radius: 4px;
    padding: 10px 16px;
    margin-top: 6px;
    min-height: 90px;
    line-height: 20px;
    margin-bottom: 8rem;
    line-break: anywhere;
    color: ${ColorBaseDarkGray};
    background-color: ${ColorBaseGreyLight};
`

const labelStyle = css`
    font-size: 0.75rem;
    font-family: Roboto, Helvetica, Arial, sans-serif;
    font-weight: 400;
    line-height: 1;
`

export default MinutesAttendeesReadOnly
//#endregion
